/**
 * Currency utility functions for BGN/EUR conversion and formatting
 * Supports both fixed rates and dynamic settings from admin
 */

import { CurrencySettings } from '../services/settingsService';

// Default Bulgarian exchange rate (can be overridden by admin settings)
export const DEFAULT_BGN_TO_EUR_RATE = 1.95583;

// Currency symbols
export const BGN_SYMBOL = 'лв.';
export const EUR_SYMBOL = '€';

/**
 * Converts BGN amount to EUR
 * @param bgnAmount - The amount in BGN
 * @returns The equivalent amount in EUR
 */
export const convertBgnToEur = (bgnAmount: number): number => {
  return Number((bgnAmount / DEFAULT_BGN_TO_EUR_RATE).toFixed(2));
};

/**
 * Converts EUR amount to BGN
 * @param eurAmount - The amount in EUR
 * @returns The equivalent amount in BGN
 */
export const convertEurToBgn = (eurAmount: number): number => {
  return Number((eurAmount * DEFAULT_BGN_TO_EUR_RATE).toFixed(2));
};


/**
 * Formats price in both currencies
 * @param bgnAmount - The amount in BGN
 * @returns Object with both formatted prices
 */
export const formatDualCurrency = (bgnAmount: number) => {
  const eurAmount = convertBgnToEur(bgnAmount);
  return {
    bgn: `${bgnAmount.toFixed(2)} ${BGN_SYMBOL}`,
    eur: `(${eurAmount.toFixed(2)} ${EUR_SYMBOL})`,
    bgnAmount,
    eurAmount,
  };
};

/**
 * Currency interface for dual currency display
 */
export interface CurrencyDisplay {
  bgn: string;
  eur: string;
  bgnAmount: number;
  eurAmount: number;
}

/**
 * Type definitions for currency
 */
export type Currency = 'BGN' | 'EUR';

/**
 * Currency configuration
 */
export const CURRENCY_CONFIG = {
  primary: 'EUR' as Currency,
  secondary: 'BGN' as Currency,
  exchangeRate: DEFAULT_BGN_TO_EUR_RATE,
  symbols: {
    BGN: BGN_SYMBOL,
    EUR: EUR_SYMBOL,
  },
} as const;

// ==================== NEW DYNAMIC CURRENCY FUNCTIONS ====================

/**
 * Format price according to admin currency display settings
 * @param price - Price in BGN
 * @param settings - Currency settings from admin
 * @returns Formatted price string
 */
export const formatPrice = (price: number, settings: CurrencySettings): string => {
  if (settings.currency_display_mode === 'eur_only') {
    // Show only EUR
    const eurPrice = price / settings.bgn_to_eur_rate;
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: 'EUR',
    }).format(eurPrice);
  } else {
    // Show dual currency (EUR / BGN) - EUR is primary since 01.01.2026
    const eurPrice = new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: 'EUR',
    }).format(price / settings.bgn_to_eur_rate);

    const bgnPrice = new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: 'BGN',
    }).format(price);

    return `${eurPrice} / ${bgnPrice}`;
  }
};

/**
 * Default price formatter when settings are not available
 * Falls back to dual currency display
 */
export const formatPriceDefault = (price: number, bgnToEurRate = DEFAULT_BGN_TO_EUR_RATE): string => {
  const settings: CurrencySettings = {
    currency_display_mode: 'dual',
    bgn_to_eur_rate: bgnToEurRate
  };
  
  return formatPrice(price, settings);
};

/**
 * Convert BGN to EUR using dynamic rate
 */
export const convertBgnToEurDynamic = (bgnAmount: number, rate: number): number => {
  return bgnAmount / rate;
};

/**
 * Convert EUR to BGN using dynamic rate
 */
export const convertEurToBgnDynamic = (eurAmount: number, rate: number): number => {
  return eurAmount * rate;
};

/**
 * Get currency symbol based on display mode
 */
export const getCurrencySymbol = (settings: CurrencySettings): string => {
  return settings.currency_display_mode === 'eur_only' ? EUR_SYMBOL : BGN_SYMBOL;
};

// ==================== BACKWARD COMPATIBILITY FUNCTIONS ====================
// These functions are already defined above for compatibility